import { FormField } from "@comps/ui/form"
import { useTranslation } from "react-i18next";
import { TW_FieldSection_Label, TW_ChoiceOption, TW_ChoiceOptionColor, TW_ChoiceOptionLabel, TW_ChoiceOptionDelete, TW_NewChoiceOptionInput, TW_ChoiceConfigurationDiv, TW_ChoiceOptionColorTrigger, TW_ColorSelectorPopoverContent, TW_ChoiceOptionModify, TW_ChoiceOptionValidate, TW_ChoiceOptionInput, TW_ChoiceOptionValidateDisabled } from "@utils/twClasses";
import { CircleCheck, Trash2, Check, Pencil, X, GripVertical } from "lucide-react";
import { Input } from "@comps/ui/input";
import { FieldChoice, TYPE_ITEMS_CHOICE_OPTIONS } from "@dto/fields";
import { FieldTypeItemsFormProps } from "./FieldTypeItemsForm";
import { Label } from "@comps/ui/label";
import { useEffect, useRef, useState } from "react";
import { ColorSelector, FieldColorType, getRandomColor } from "@utils/colors";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { useModelStore } from "src/store";


export function ChoiceTypeItemsForm({ field, form, onTypeItemsChange, setError }: FieldTypeItemsFormProps) {
    const { t } = useTranslation();
    const [newOption, setNewOption] = useState("");
    const [choiceOptions, setChoiceOptions] = useState<FieldChoice[]>([]);
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const dragIndexRef = useRef<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    useEffect(() => {
        setChoiceOptions(field.typeItems?.get(TYPE_ITEMS_CHOICE_OPTIONS) || []);
    }, [field.typeItems]);

    const onNewOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewOption(event.target.value);
    }

    const onNewOptionValidatedKeyBoard = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && newOption.length > 0) {
            event.preventDefault();
            event.stopPropagation();
            onNewOptionValidated();
        }
    }

    function onNewOptionValidatedButton() {
        if (newOption.length > 0) {
            onNewOptionValidated();
        }
    }

    function onNewOptionValidated() {
        if (choiceOptions.find((option: FieldChoice) => option.name === newOption)) {
            setError(t("field.choice.new.option.duplicate"));
            return;
        }
        const optionName = newOption;
        const optionColor = getRandomColor(FieldColorType);
        const option = { name: optionName, color: optionColor };
        const newChoiceOptions = [...choiceOptions, option];
        const newTypeItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        newTypeItems.set(TYPE_ITEMS_CHOICE_OPTIONS, newChoiceOptions);
        form.setValue("fieldTypeItems", newTypeItems);
        onTypeItemsChange(newTypeItems);
        setNewOption("");
        setChoiceOptions(newChoiceOptions);
    }

    const onOptionValidated = (optionName: string, newOptionName: string) => {
        const newChoiceOptions = choiceOptions.map((choiceOption) => {
            if (choiceOption.name === optionName) {
                return { ...choiceOption, name: newOptionName };
            }
            else {
                return choiceOption;
            }
        });
        const newTypeItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        newTypeItems.set(TYPE_ITEMS_CHOICE_OPTIONS, newChoiceOptions);
        form.setValue("fieldTypeItems", newTypeItems);
        onTypeItemsChange(newTypeItems);
        setChoiceOptions(newChoiceOptions);
    }

    const onDeleteOption = (optionName: string) => {
        const newChoiceOptions = choiceOptions.filter((option: FieldChoice) => option.name !== optionName);
        const newTypeItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        newTypeItems.set(TYPE_ITEMS_CHOICE_OPTIONS, newChoiceOptions);
        form.setValue("fieldTypeItems", newTypeItems);
        onTypeItemsChange(newTypeItems);
        setChoiceOptions(newChoiceOptions);
    }


    const onColorSelected = (option: FieldChoice, newColor: string) => {
        const newOption = { ...option, color: newColor };
        const newChoiceOptions = choiceOptions.map((fieldChoice: FieldChoice) => fieldChoice.name === newOption.name ? newOption : fieldChoice);
        const newTypeItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        newTypeItems.set(TYPE_ITEMS_CHOICE_OPTIONS, newChoiceOptions);
        form.setValue("fieldTypeItems", newTypeItems);
        onTypeItemsChange(newTypeItems);
        setChoiceOptions(newChoiceOptions);
    }

    const onDragStart = (index: number) => {
        dragIndexRef.current = index;
    }

    const onDragOver = (index: number, event: React.DragEvent) => {
        event.preventDefault();
        setDragOverIndex(index);
    }

    const onDrop = (dropIndex: number) => {
        const fromIndex = dragIndexRef.current;
        if (fromIndex === null || fromIndex === dropIndex) {
            dragIndexRef.current = null;
            setDragOverIndex(null);
            return;
        }
        const newChoiceOptions = [...choiceOptions];
        const [moved] = newChoiceOptions.splice(fromIndex, 1);
        newChoiceOptions.splice(dropIndex, 0, moved);
        const newTypeItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        newTypeItems.set(TYPE_ITEMS_CHOICE_OPTIONS, newChoiceOptions);
        form.setValue("fieldTypeItems", newTypeItems);
        onTypeItemsChange(newTypeItems);
        setChoiceOptions(newChoiceOptions);
        dragIndexRef.current = null;
        setDragOverIndex(null);
    }

    const onDragEnd = () => {
        dragIndexRef.current = null;
        setDragOverIndex(null);
    }

    const scrollStyle = {
        scrollbarColor: wsColor + " " + wsLightColor
    }

    const isNewOptionValidatedDisabled = newOption.length === 0;
    return (
        <FormField
            control={form.control}
            name="fieldTypeItems"
            render={({ field }) => {
                return (
                    <>
                        <p className={TW_FieldSection_Label}>{t(`field.choice.options`)}</p>
                        <div className={TW_ChoiceConfigurationDiv} style={scrollStyle}>
                            {choiceOptions?.map((option: FieldChoice, index: number) => {
                                return (
                                    <ChoiceItem
                                        key={option.name}
                                        option={option}
                                        index={index}
                                        isDragOver={dragOverIndex === index}
                                        onColorSelected={onColorSelected}
                                        onDeleteOption={onDeleteOption}
                                        onOptionValidated={onOptionValidated}
                                        onDragStart={onDragStart}
                                        onDragOver={onDragOver}
                                        onDrop={onDrop}
                                        onDragEnd={onDragEnd}
                                    />
                                )
                            })}
                            <div className={"flex flex-row items-center " + TW_ChoiceOption}>
                                <Input className={TW_NewChoiceOptionInput} placeholder={t("field.choice.new.option.placeholder")} onChange={onNewOptionChange} onKeyDown={onNewOptionValidatedKeyBoard} value={newOption} />
                                <Check className={isNewOptionValidatedDisabled ? TW_ChoiceOptionValidateDisabled : TW_ChoiceOptionValidate} onClick={onNewOptionValidatedButton} />
                            </div>
                        </div>
                    </>
                )
            }
            }
        />
    )
}


export interface ChoiceItemProps {
    option: FieldChoice,
    index: number,
    isDragOver: boolean,
    onColorSelected: (option: FieldChoice, color: string) => void,
    onDeleteOption: (optionName: string) => void,
    onOptionValidated: (optionName: string, newOptionName: string) => void,
    onDragStart: (index: number) => void,
    onDragOver: (index: number, event: React.DragEvent) => void,
    onDrop: (index: number) => void,
    onDragEnd: () => void,
}

export function ChoiceItem({ option, index, isDragOver, onColorSelected, onDeleteOption, onOptionValidated, onDragStart, onDragOver, onDrop, onDragEnd }: ChoiceItemProps) {
    const [isColorSelectorOpen, setIsColorSelectorOpen] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    const [newOptionName, setNewOptionName] = useState(option.name);
    const validateButtonRef = useRef(false);

    const setColor = (color: string) => {
        onColorSelected(option, color);
        setIsColorSelectorOpen(false);
    }

    function onEditClick() {
        setIsEdited(true)
    }

    function onCancel() {
        setNewOptionName(option.name);
        setIsEdited(false);
    }

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (validateButtonRef.current) {
            validateButtonRef.current = false; // reset
            return; // don't close if the click comes from the inside
        }
        setNewOptionName(option.name);
        setIsEdited(false);
    }

    const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setNewOptionName(e.target.value)
    }

    const onOptionValidatedKeyBoard = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && newOptionName.length > 0) {
            event.preventDefault();
            event.stopPropagation();
            onOptionValidated(option.name, newOptionName);
            setIsEdited(false)
        }
    }

    const onOptionValidatedButton = (event: React.MouseEvent) => {
        // event.stopPropagation();
        // event.preventDefault();
        onOptionValidated(option.name, newOptionName);
        setIsEdited(false)
    }

    const onDeleteClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        onDeleteOption(option.name);
    }

    const markInternalMouseDown = () => { validateButtonRef.current = true; };

    const isOptionValidatedDisabled = newOptionName.length === 0;
    return (
        <div
            key={option.name}
            className={TW_ChoiceOption + (isDragOver ? " border-dashed opacity-50" : "")}
            style={{ borderColor: option.color }}
            onBlur={handleBlur}
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={(e) => onDragOver(index, e)}
            onDrop={() => onDrop(index)}
            onDragEnd={onDragEnd}
        >
            <GripVertical className="w-4 h-4 text-gray-300 cursor-grab shrink-0" />
            <Popover open={isColorSelectorOpen} onOpenChange={setIsColorSelectorOpen}>
                <PopoverTrigger className={TW_ChoiceOptionColorTrigger}>
                    <CircleCheck className={TW_ChoiceOptionColor} style={{ color: option.color }} />
                </PopoverTrigger>
                <PopoverContent className={TW_ColorSelectorPopoverContent}>
                    <ColorSelector onColorSelected={setColor} colorType={FieldColorType} />
                </PopoverContent>
            </Popover>
            {!isEdited
                ?
                <>
                    <Label className={TW_ChoiceOptionLabel} onDoubleClick={onEditClick}>{option.name}</Label>
                    <Pencil className={TW_ChoiceOptionModify} onClick={onEditClick} />
                    <Trash2 className={TW_ChoiceOptionDelete} onClick={onDeleteClick} />
                </>
                :
                <>
                    <Input autoFocus className={TW_ChoiceOptionInput} value={newOptionName} onChange={onValueChange} onKeyDown={onOptionValidatedKeyBoard} />
                    <Check className={isOptionValidatedDisabled ? TW_ChoiceOptionValidateDisabled : TW_ChoiceOptionValidate} onMouseDown={markInternalMouseDown} onClick={onOptionValidatedButton} />
                    <X className={TW_ChoiceOptionDelete} onClick={onCancel} />
                </>
            }
        </div>
    )
}